import { useState, useEffect, useCallback } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { mockSiteVisitRequests, type SiteVisitRequest, type VisitStatus, type JobCardVisit } from "@/mocks/sitevisits";
import { adminApi, jobCardsApi } from "@/services/api";

const STATUS_CONFIG: Record<VisitStatus, { label: string; color: string; bg: string; icon: string }> = {
  Pending: { label: "Pending", color: "#f59e0b", bg: "#fffbeb", icon: "ri-time-line" },
  Confirmed: { label: "Confirmed", color: "#f59e0b", bg: "#fffbeb", icon: "ri-checkbox-circle-line" },
  Rejected: { label: "Rejected", color: "#ef4444", bg: "#fef2f2", icon: "ri-close-circle-line" },
  Completed: { label: "Completed", color: "#64748b", bg: "#f8fafc", icon: "ri-check-double-line" },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
}

interface ConfirmModalProps {
  visit: SiteVisitRequest;
  onConfirm: (notes: string) => void;
  onClose: () => void;
}

function ConfirmModal({ visit, onConfirm, onClose }: ConfirmModalProps) {
  const [notes, setNotes] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-100 p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl">
            <i className="ri-checkbox-circle-line text-amber-600 text-xl" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Confirm Site Visit</h3>
            <p className="text-xs text-gray-500">{visit.refNo} · {visit.clientName}</p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Preferred Date</span>
            <span className="font-medium text-gray-800">{formatDate(visit.preferredDate)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Time Slot</span>
            <span className="font-medium text-gray-800">{visit.timeSlot}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Attendees</span>
            <span className="font-medium text-gray-800">{visit.attendees} person(s)</span>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Confirmation Notes (optional)</label>
          <textarea
            rows={3}
            maxLength={500}
            placeholder="e.g. Team lead will be present. Please bring site access pass."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(notes)}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Confirm Visit
          </button>
        </div>
      </div>
    </div>
  );
}

interface RejectModalProps {
  visit: SiteVisitRequest;
  onReject: (reason: string) => void;
  onClose: () => void;
}

function RejectModal({ visit, onReject, onClose }: RejectModalProps) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-100 p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl">
            <i className="ri-close-circle-line text-red-500 text-xl" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Reject Site Visit</h3>
            <p className="text-xs text-gray-500">{visit.refNo} · {visit.clientName}</p>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Reason for Rejection *</label>
          <textarea
            rows={4}
            maxLength={500}
            placeholder="Explain why this visit request is being rejected..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-1">{reason.length}/500</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={() => reason.trim() && onReject(reason)}
            disabled={!reason.trim()}
            className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reject Visit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SiteVisitRequestsPage() {
  const [activeSection, setActiveSection] = useState<"client_requests" | "job_visits">("client_requests");
  const [visits, setVisits] = useState<SiteVisitRequest[]>([]);
  const [jobVisits, setJobVisits] = useState<JobCardVisit[]>([]);
  const [jobVisitsLoading, setJobVisitsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<VisitStatus | "All">("All");
  const [selectedVisit, setSelectedVisit] = useState<SiteVisitRequest | null>(null);
  const [selectedJobVisit, setSelectedJobVisit] = useState<JobCardVisit | null>(null);
  const [confirmModal, setConfirmModal] = useState<SiteVisitRequest | null>(null);
  const [rejectModal, setRejectModal] = useState<SiteVisitRequest | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load client-submitted site visit requests ─────────────────────────────
  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSiteVisits({ limit: 200 });
      const rows = (res?.data ?? []) as Record<string, unknown>[];
      if (rows.length > 0) {
        const mapped: SiteVisitRequest[] = rows.map((r) => ({
          id: r.id as number,
          refNo: (r.ref_no || r.refNo) as string,
          clientName: (r.client_name || r.clientName) as string,
          clientEmail: (r.client_email || r.clientEmail) as string,
          clientPhone: (r.client_phone || r.clientPhone) as string,
          company: r.company as string,
          jobRef: (r.job_ref || r.jobRef) as string,
          serviceType: (r.service_type || r.serviceType) as string,
          preferredDate: (r.preferred_date || r.preferredDate) as string,
          alternateDate: (r.alternate_date || r.alternateDate) as string,
          timeSlot: (r.time_slot || r.timeSlot) as string,
          attendees: r.attendees as number,
          siteAddress: (r.site_address || r.siteAddress) as string,
          purpose: r.purpose as string,
          specialRequirements: (r.special_requirements || r.specialRequirements || "None") as string,
          status: r.status as VisitStatus,
          submittedAt: (r.submitted_at || r.submittedAt) as string,
          confirmedBy: (r.confirmed_by || r.confirmedBy) as string | undefined,
          confirmedAt: (r.confirmed_at || r.confirmedAt) as string | undefined,
          rejectionReason: (r.rejection_reason || r.rejectionReason) as string | undefined,
          notes: r.notes as string | undefined,
        }));
        setVisits(mapped);
      } else {
        setVisits(mockSiteVisitRequests);
      }
    } catch {
      setVisits(mockSiteVisitRequests);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load job cards with field_visit_required = true ───────────────────────
  const loadJobVisits = useCallback(async () => {
    setJobVisitsLoading(true);
    try {
      const res = await jobCardsApi.getAll({ limit: 200 } as Parameters<typeof jobCardsApi.getAll>[0]);
      const rows = (res?.data ?? res ?? []) as Record<string, unknown>[];
      const filtered = (Array.isArray(rows) ? rows : []).filter(
        (r) => r.field_visit_required === true || r.fieldVisitRequired === true
      );
      const mapped: JobCardVisit[] = filtered.map((r) => ({
        id: r.id as number,
        jobNo: (r.job_no || r.jobNo) as string,
        projectTitle: (r.title || r.projectTitle) as string,
        clientName: (r.client_name || r.clientName) as string,
        clientPhone: (r.client_phone || r.clientPhone || "") as string,
        clientEmail: (r.client_email || r.clientEmail || "") as string,
        clientCompany: (r.client_company || r.clientCompany || "") as string,
        surveyLocation: (r.location || r.surveyLocation || "") as string,
        services: (r.services as string[]) || [],
        assignedDate: (r.assigned_date || r.assignedDate || "") as string,
        completionDate: (r.due_date || r.completionDate || "") as string,
        priority: (r.priority || "Medium") as string,
        status: (r.status || "Pending") as string,
        fieldVisitRequired: true,
      }));
      setJobVisits(mapped);
    } catch {
      setJobVisits([]);
    } finally {
      setJobVisitsLoading(false);
    }
  }, []);

  useEffect(() => { loadVisits(); }, [loadVisits]);
  useEffect(() => { loadJobVisits(); }, [loadJobVisits]);

  const handleConfirm = async (visit: SiteVisitRequest, notes: string) => {
    try {
      await adminApi.confirmSiteVisit(visit.id, notes);
      await loadVisits();
      if (selectedVisit?.id === visit.id) {
        setSelectedVisit((prev) => prev ? { ...prev, status: "Confirmed", confirmedBy: "Admin", confirmedAt: new Date().toISOString(), notes } : prev);
      }
      showToast(`Visit ${visit.refNo} confirmed successfully`);
    } catch {
      setVisits((prev) => prev.map((v) => v.id === visit.id ? { ...v, status: "Confirmed", confirmedBy: "Admin", confirmedAt: new Date().toISOString(), notes } : v));
      if (selectedVisit?.id === visit.id) {
        setSelectedVisit((prev) => prev ? { ...prev, status: "Confirmed", confirmedBy: "Admin", confirmedAt: new Date().toISOString(), notes } : prev);
      }
      showToast(`Visit ${visit.refNo} confirmed`);
    }
    setConfirmModal(null);
  };

  const handleReject = async (visit: SiteVisitRequest, reason: string) => {
    try {
      await adminApi.rejectSiteVisit(visit.id, reason);
      await loadVisits();
      if (selectedVisit?.id === visit.id) {
        setSelectedVisit((prev) => prev ? { ...prev, status: "Rejected", rejectionReason: reason } : prev);
      }
      showToast(`Visit ${visit.refNo} rejected`, "error");
    } catch {
      setVisits((prev) => prev.map((v) => v.id === visit.id ? { ...v, status: "Rejected", rejectionReason: reason } : v));
      if (selectedVisit?.id === visit.id) {
        setSelectedVisit((prev) => prev ? { ...prev, status: "Rejected", rejectionReason: reason } : prev);
      }
      showToast(`Visit ${visit.refNo} rejected`, "error");
    }
    setRejectModal(null);
  };

  const handleMarkCompleted = async (visit: SiteVisitRequest) => {
    try {
      await adminApi.completeSiteVisit(visit.id);
      await loadVisits();
      if (selectedVisit?.id === visit.id) {
        setSelectedVisit((prev) => prev ? { ...prev, status: "Completed" } : prev);
      }
      showToast(`Visit ${visit.refNo} marked as completed`);
    } catch {
      setVisits((prev) => prev.map((v) => v.id === visit.id ? { ...v, status: "Completed" } : v));
      if (selectedVisit?.id === visit.id) {
        setSelectedVisit((prev) => prev ? { ...prev, status: "Completed" } : prev);
      }
      showToast(`Visit ${visit.refNo} marked as completed`);
    }
  };

  const filtered = filterStatus === "All" ? visits : visits.filter((v) => v.status === filterStatus);
  const counts = {
    All: visits.length,
    Pending: visits.filter((v) => v.status === "Pending").length,
    Confirmed: visits.filter((v) => v.status === "Confirmed").length,
    Rejected: visits.filter((v) => v.status === "Rejected").length,
    Completed: visits.filter((v) => v.status === "Completed").length,
  };

  const priorityColors: Record<string, { bg: string; text: string }> = {
    low: { bg: "#f0fdf4", text: "#16a34a" },
    medium: { bg: "#fffbeb", text: "#d97706" },
    high: { bg: "#fef2f2", text: "#dc2626" },
    critical: { bg: "#fdf2f8", text: "#9d174d" },
  };

  return (
    <ERPLayout title="Site Visit Requests" subtitle="Review and manage client-submitted site visit requests">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${toast.type === "success" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-red-50 border-red-200 text-red-700"}`}>
          <span className="w-5 h-5 flex items-center justify-center">
            <i className={toast.type === "success" ? "ri-checkbox-circle-line text-lg" : "ri-close-circle-line text-lg"} />
          </span>
          {toast.msg}
        </div>
      )}

      {/* Confirm / Reject Modals */}
      {confirmModal && (
        <ConfirmModal
          visit={confirmModal}
          onConfirm={(notes) => handleConfirm(confirmModal, notes)}
          onClose={() => setConfirmModal(null)}
        />
      )}
      {rejectModal && (
        <RejectModal
          visit={rejectModal}
          onReject={(reason) => handleReject(rejectModal, reason)}
          onClose={() => setRejectModal(null)}
        />
      )}

      <div className="p-6 space-y-6">
        {/* Section Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          <button
            onClick={() => { setActiveSection("client_requests"); setSelectedJobVisit(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeSection === "client_requests" ? "bg-white text-amber-700 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-event-line text-sm" /></span>
            Client Requests
            {counts.Pending > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{counts.Pending}</span>
            )}
          </button>
          <button
            onClick={() => { setActiveSection("job_visits"); setSelectedVisit(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeSection === "job_visits" ? "bg-white text-amber-700 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-sm" /></span>
            Job Card Visits
            {jobVisits.length > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{jobVisits.length}</span>
            )}
          </button>
        </div>

        {/* ── CLIENT REQUESTS SECTION ─────────────────────────────────────── */}
        {activeSection === "client_requests" && (
          <>
            {/* KPI Strip */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: "Total Requests", value: counts.All, icon: "ri-calendar-event-line", color: "text-gray-600", bg: "bg-gray-100" },
                { label: "Pending Review", value: counts.Pending, icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50" },
                { label: "Confirmed", value: counts.Confirmed, icon: "ri-checkbox-circle-line", color: "text-amber-600", bg: "bg-amber-50" },
                { label: "Completed", value: counts.Completed, icon: "ri-check-double-line", color: "text-gray-500", bg: "bg-gray-100" },
              ].map((kpi) => (
                <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">{kpi.label}</span>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${kpi.bg}`}>
                      <i className={`${kpi.icon} text-lg ${kpi.color}`} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{loading ? "—" : kpi.value}</p>
                </div>
              ))}
            </div>

            {!loading && counts.Pending > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                  <i className="ri-notification-3-line text-amber-600" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">{counts.Pending} visit request{counts.Pending > 1 ? "s" : ""} awaiting your review</p>
                  <p className="text-xs text-amber-600 mt-0.5">Clients are waiting for confirmation. Please review and respond promptly.</p>
                </div>
                <button onClick={() => setFilterStatus("Pending")} className="text-xs font-semibold text-amber-700 hover:underline cursor-pointer whitespace-nowrap">
                  View Pending →
                </button>
              </div>
            )}

            <div className="grid grid-cols-3 gap-6">
              {/* Left: List */}
              <div className="col-span-1 space-y-3">
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
                  {(["All", "Pending", "Confirmed", "Rejected", "Completed"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setFilterStatus(s)}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {s}
                      {!loading && counts[s] > 0 && (
                        <span className={`ml-1 px-1 py-0.5 rounded-full text-xs font-bold ${s === "Pending" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>
                          {counts[s]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {loading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
                        <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filtered.map((visit) => {
                      const cfg = STATUS_CONFIG[visit.status];
                      const isSelected = selectedVisit?.id === visit.id;
                      return (
                        <button
                          key={visit.id}
                          onClick={() => setSelectedVisit(visit)}
                          className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${isSelected ? "border-amber-300 bg-amber-50/50" : "bg-white border-gray-100 hover:border-gray-200"}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{visit.clientName}</p>
                              <p className="text-xs text-gray-500 truncate">{visit.company}</p>
                            </div>
                            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ color: cfg.color, background: cfg.bg }}>
                              <i className={`${cfg.icon} text-xs`} />
                              {cfg.label}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-file-list-3-line" /></span>
                              <span className="truncate">{visit.jobRef}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line" /></span>
                              {formatDate(visit.preferredDate)} · {visit.timeSlot}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line" /></span>
                              Submitted {formatDateTime(visit.submittedAt)}
                            </div>
                          </div>
                          {visit.status === "Pending" && (
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmModal(visit); }}
                                className="flex-1 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setRejectModal(visit); }}
                                className="flex-1 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </button>
                      );
                    })}
                    {filtered.length === 0 && (
                      <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                        <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                          <i className="ri-calendar-event-line text-xl text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500">No {filterStatus !== "All" ? filterStatus.toLowerCase() : ""} requests</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Detail Panel */}
              <div className="col-span-2">
                {selectedVisit ? (
                  <VisitDetailPanel
                    visit={selectedVisit}
                    onConfirm={() => setConfirmModal(selectedVisit)}
                    onReject={() => setRejectModal(selectedVisit)}
                    onMarkCompleted={() => handleMarkCompleted(selectedVisit)}
                  />
                ) : (
                  <div className="bg-white rounded-xl border border-gray-100 h-full flex items-center justify-center p-12">
                    <div className="text-center">
                      <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                        <i className="ri-calendar-event-line text-2xl text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm font-medium">Select a request to view details</p>
                      <p className="text-gray-400 text-xs mt-1">Click any visit request from the list</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── JOB CARD VISITS SECTION ─────────────────────────────────────── */}
        {activeSection === "job_visits" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Job Cards with Field Visit Required</h3>
                <p className="text-xs text-gray-500 mt-0.5">These jobs were created with "Field Visit Required" checked</p>
              </div>
              <button
                onClick={loadJobVisits}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-refresh-line text-sm ${jobVisitsLoading ? "animate-spin" : ""}`} />
                </span>
                Refresh
              </button>
            </div>

            {jobVisitsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" />
                    <div className="h-3 bg-gray-100 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : jobVisits.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-map-pin-line text-2xl text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm font-medium">No job cards with field visit required</p>
                <p className="text-gray-400 text-xs mt-1">When creating a job card, check "Field Visit Required" to see it here</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                {/* Left: Job Visit List */}
                <div className="col-span-1 space-y-2">
                  {jobVisits.map((jv) => {
                    const pc = priorityColors[jv.priority?.toLowerCase()] || { bg: "#f3f4f6", text: "#374151" };
                    const isSelected = selectedJobVisit?.id === jv.id;
                    return (
                      <button
                        key={jv.id}
                        onClick={() => setSelectedJobVisit(jv)}
                        className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${isSelected ? "border-amber-300 bg-amber-50/50" : "bg-white border-gray-100 hover:border-gray-200"}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-mono font-bold text-gray-500">{jv.jobNo}</p>
                            <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{jv.projectTitle}</p>
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ background: pc.bg, color: pc.text }}>
                            {jv.priority}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-user-line" /></span>
                            <span className="truncate">{jv.clientName}</span>
                          </div>
                          {jv.surveyLocation && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-line" /></span>
                              <span className="truncate">{jv.surveyLocation}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line" /></span>
                            Due: {jv.completionDate || "Not set"}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                          <span className="text-xs text-amber-700 font-medium">Field Visit Required</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Right: Job Visit Detail */}
                <div className="col-span-2">
                  {selectedJobVisit ? (
                    <JobVisitDetailPanel visit={selectedJobVisit} />
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-100 h-full flex items-center justify-center p-12">
                      <div className="text-center">
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                          <i className="ri-map-pin-line text-2xl text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm font-medium">Select a job to view details</p>
                        <p className="text-gray-400 text-xs mt-1">Click any job card from the list</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ERPLayout>
  );
}

interface VisitDetailPanelProps {
  visit: SiteVisitRequest;
  onConfirm: () => void;
  onReject: () => void;
  onMarkCompleted: () => void;
}

function VisitDetailPanel({ visit, onConfirm, onReject, onMarkCompleted }: VisitDetailPanelProps) {
  const cfg = STATUS_CONFIG[visit.status];

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-gray-900">{visit.refNo}</h2>
            <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ color: cfg.color, background: cfg.bg }}>
              <i className={`${cfg.icon} text-xs`} />
              {cfg.label}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">Submitted {new Date(visit.submittedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}</p>
        </div>
        {visit.status === "Pending" && (
          <div className="flex gap-2">
            <button
              onClick={onConfirm}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line" /></span>
              Confirm
            </button>
            <button
              onClick={onReject}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-circle-line" /></span>
              Reject
            </button>
          </div>
        )}
        {visit.status === "Confirmed" && (
          <button
            onClick={onMarkCompleted}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-double-line" /></span>
            Mark Completed
          </button>
        )}
      </div>

      <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 280px)" }}>
        {/* Client Info */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Client Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400">Client Name</p>
                <p className="text-sm font-semibold text-gray-900">{visit.clientName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Company</p>
                <p className="text-sm font-medium text-gray-800">{visit.company}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Job Reference</p>
                <p className="text-sm font-medium text-gray-800">{visit.jobRef}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400">Email</p>
                <a href={`mailto:${visit.clientEmail}`} className="text-sm font-medium text-amber-600 hover:underline">{visit.clientEmail}</a>
              </div>
              <div>
                <p className="text-xs text-gray-400">Phone</p>
                <p className="text-sm font-medium text-gray-800">{visit.clientPhone}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Service Type</p>
                <p className="text-sm font-medium text-gray-800">{visit.serviceType}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Visit Schedule */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Visit Schedule</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
              <p className="text-xs text-amber-600 font-medium mb-1">Preferred Date</p>
              <p className="text-sm font-bold text-gray-900">{new Date(visit.preferredDate).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <p className="text-xs text-gray-500 font-medium mb-1">Alternate Date</p>
              <p className="text-sm font-bold text-gray-900">{new Date(visit.alternateDate).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <p className="text-xs text-gray-500 font-medium mb-1">Attendees</p>
              <p className="text-sm font-bold text-gray-900">{visit.attendees} person{visit.attendees > 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="mt-3 bg-amber-50 rounded-xl p-3 border border-amber-100 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-time-line text-amber-600" /></span>
            <div>
              <p className="text-xs text-amber-600 font-medium">Time Slot</p>
              <p className="text-sm font-bold text-gray-900">{visit.timeSlot}</p>
            </div>
          </div>
        </div>

        {/* Site Address */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Site Location</h3>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <span className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0 mt-0.5">
              <i className="ri-map-pin-line text-amber-600" />
            </span>
            <div>
              <p className="text-sm font-medium text-gray-800">{visit.siteAddress}</p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visit.siteAddress)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-xs text-amber-600 hover:underline mt-1 inline-flex items-center gap-1"
              >
                <i className="ri-external-link-line text-xs" />
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>

        {/* Purpose */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Purpose of Visit</h3>
          <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-4 border border-gray-100 leading-relaxed">{visit.purpose}</p>
        </div>

        {/* Special Requirements */}
        {visit.specialRequirements && visit.specialRequirements !== "None" && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Special Requirements</h3>
            <div className="flex items-start gap-2.5 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-alert-line text-amber-600 text-sm" /></span>
              <p className="text-sm text-gray-700 leading-relaxed">{visit.specialRequirements}</p>
            </div>
          </div>
        )}

        {/* Status Info */}
        {(visit.status === "Confirmed" || visit.status === "Completed") && visit.confirmedBy && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Confirmation Details</h3>
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Confirmed by</span>
                <span className="font-medium text-gray-800">{visit.confirmedBy}</span>
              </div>
              {visit.confirmedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Confirmed at</span>
                  <span className="font-medium text-gray-800">{new Date(visit.confirmedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}</span>
                </div>
              )}
              {visit.notes && (
                <div className="pt-2 border-t border-amber-200">
                  <p className="text-xs text-gray-500 mb-1">Notes</p>
                  <p className="text-sm text-gray-700">{visit.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {visit.status === "Rejected" && visit.rejectionReason && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Rejection Reason</h3>
            <div className="flex items-start gap-2.5 p-4 bg-red-50 rounded-xl border border-red-100">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-close-circle-line text-red-500 text-sm" /></span>
              <p className="text-sm text-gray-700 leading-relaxed">{visit.rejectionReason}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function JobVisitDetailPanel({ visit }: { visit: JobCardVisit }) {
  const priorityColors: Record<string, { bg: string; text: string }> = {
    low: { bg: "#f0fdf4", text: "#16a34a" },
    medium: { bg: "#fffbeb", text: "#d97706" },
    high: { bg: "#fef2f2", text: "#dc2626" },
    critical: { bg: "#fdf2f8", text: "#9d174d" },
  };
  const pc = priorityColors[visit.priority?.toLowerCase()] || { bg: "#f3f4f6", text: "#374151" };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-gray-900">{visit.jobNo}</h2>
            <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium bg-amber-50 text-amber-700">
              <i className="ri-map-pin-line text-xs" />
              Field Visit Required
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize" style={{ background: pc.bg, color: pc.text }}>
              {visit.priority} Priority
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{visit.projectTitle}</p>
        </div>
        <a
          href={`/job-cards/${visit.id}`}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm" /></span>
          Open Job Card
        </a>
      </div>

      <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 280px)" }}>
        {/* Project Information */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Project Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400">Project Title</p>
                <p className="text-sm font-semibold text-gray-900">{visit.projectTitle || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Survey Location</p>
                <p className="text-sm font-medium text-gray-800">{visit.surveyLocation || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Assigned Date</p>
                <p className="text-sm font-medium text-gray-800">{visit.assignedDate || "—"}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400">Completion Date</p>
                <p className="text-sm font-medium text-gray-800">{visit.completionDate || "Not set"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Job Status</p>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 capitalize">{visit.status}</span>
              </div>
              <div>
                <p className="text-xs text-gray-400">Priority</p>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize" style={{ background: pc.bg, color: pc.text }}>{visit.priority}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Services */}
        {visit.services && visit.services.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Services Required</h3>
            <div className="flex flex-wrap gap-2">
              {visit.services.map((s, i) => (
                <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full border border-amber-100 font-medium">
                  {typeof s === "string" ? s : (s as Record<string, string>).name || String(s)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Client Contact Details */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Client Contact Details</h3>
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {visit.clientName?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?"}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{visit.clientName || "—"}</p>
                {visit.clientCompany && <p className="text-xs text-gray-500">{visit.clientCompany}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
              {visit.clientPhone && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0">
                    <i className="ri-phone-line text-amber-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Phone</p>
                    <a href={`tel:${visit.clientPhone}`} className="text-sm font-medium text-gray-800 hover:text-amber-600">{visit.clientPhone}</a>
                  </div>
                </div>
              )}
              {visit.clientEmail && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0">
                    <i className="ri-mail-line text-amber-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <a href={`mailto:${visit.clientEmail}`} className="text-sm font-medium text-amber-600 hover:underline truncate block max-w-[140px]">{visit.clientEmail}</a>
                  </div>
                </div>
              )}
            </div>
            {visit.surveyLocation && (
              <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
                <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-amber-600 text-sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Site Location</p>
                  <p className="text-sm font-medium text-gray-800">{visit.surveyLocation}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visit.surveyLocation)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-xs text-amber-600 hover:underline mt-0.5 inline-flex items-center gap-1"
                  >
                    <i className="ri-external-link-line text-xs" />
                    Open in Google Maps
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* WhatsApp Quick Contact */}
        {visit.clientPhone && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <span className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
              <i className="ri-whatsapp-line text-amber-700 text-lg" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Quick WhatsApp Contact</p>
              <p className="text-xs text-amber-600 mt-0.5">Send a message to the client about the field visit</p>
            </div>
            <a
              href={`https://wa.me/${visit.clientPhone.replace(/[\s\-()]/g, "").replace(/^\+/, "")}?text=${encodeURIComponent(`Hello ${visit.clientName}, this is regarding the field visit for project "${visit.projectTitle}" (${visit.jobNo}). Please confirm your availability.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 cursor-pointer whitespace-nowrap transition-all"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-whatsapp-line text-sm" /></span>
              Message Client
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
